/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and international
 * treaties. Unauthorized use, reproduction, distribution, or modification of any content
 * from Knowledge for Decisions (K4D) is strictly prohibited. For inquiries regarding
 * the use or licensing of K4D's opyrighted materials, please contact us at
 * contact@k4d.com.br. "Knowledge for Decisions (K4D)" and the slogan "Using data to make
 * precise decisions" are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useCallback, useState } from 'react';
import { CalendarService } from '@services/requests/housien-services/CalendarService';

/**
 * Return shape of {@link useGoogleConnect}.
 */
export type UseGoogleConnectReturn = {
  connect: () => Promise<void>;
  isConnecting: boolean;
  error: boolean;
};

/**
 * Hook that starts the Google Calendar OAuth flow.
 *
 * It asks the backend (authenticated) for the Google `authorization_url`, then
 * navigates the browser there. The fetch carries the SPA's Bearer token, which
 * a direct browser navigation could not; hence the two-step connect.
 *
 * @param agencyId The agency initiating the connection.
 * @returns Connect trigger plus loading/error UI state.
 */
export const useGoogleConnect = (agencyId: string): UseGoogleConnectReturn => {
  const [isConnecting, setIsConnecting] = useState(false);
  const [error, setError] = useState(false);

  const connect = useCallback(async () => {
    setError(false);
    setIsConnecting(true);
    try {
      const { authorization_url } =
        await CalendarService.connectGoogle(agencyId);
      window.location.assign(authorization_url);
    } catch (err) {
      setError(true);
      setIsConnecting(false);
      throw err;
    }
  }, [agencyId]);

  return { connect, isConnecting, error };
};
